import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import {
  Badge,
  Button,
  Flex,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import { Dialog } from "@/components/shared/dialog";
import { Skill, buildSkillSlug } from "@/v2/entities/skill/Skill.model";
import styles from "./browser-skill-modal.module.scss";

export type BrowserSkillFormValues = {
  name: string;
  description: string;
  startUrl: string;
  slug?: string;
};

interface BrowserSkillModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  initialValues?: BrowserSkillFormValues;
  loading?: boolean;
  existingSkills?: Skill[];
  skillId?: string;
  onSubmit: (values: BrowserSkillFormValues) => Promise<void> | void;
}

const httpUrlSchema = z
  .string()
  .trim()
  .url()
  .refine((value) => value.startsWith("http://") || value.startsWith("https://"));

export function BrowserSkillModal({
  open,
  onOpenChange,
  mode,
  initialValues,
  loading = false,
  existingSkills = [],
  skillId,
  onSubmit,
}: BrowserSkillModalProps) {
  const defaults = useMemo(
    () => ({
      name: initialValues?.name ?? "",
      description: initialValues?.description ?? "",
      startUrl: initialValues?.startUrl ?? "",
      slug: initialValues?.slug,
    }),
    [initialValues]
  );

  const [name, setName] = useState(defaults.name);
  const [description, setDescription] = useState(defaults.description);
  const [startUrl, setStartUrl] = useState(defaults.startUrl);
  const [urlValid, setUrlValid] = useState(() => httpUrlSchema.safeParse(defaults.startUrl).success);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (open) {
      setName(defaults.name);
      setDescription(defaults.description);
      setStartUrl(defaults.startUrl);
      setUrlValid(httpUrlSchema.safeParse(defaults.startUrl).success);
      setShowErrors(false);
    }
  }, [open, defaults]);

  const slug = useMemo(() => buildSkillSlug(name || defaults.slug || ""), [name, defaults.slug]);

  const isDuplicateSlug = useMemo(
    () =>
      existingSkills.some(
        (skill) => skill.slug === slug && skill.id !== skillId
      ),
    [existingSkills, slug, skillId]
  );

  const isValid = useMemo(
    () =>
      Boolean(name.trim()) &&
      Boolean(description.trim()) &&
      urlValid &&
      !isDuplicateSlug,
    [name, description, urlValid, isDuplicateSlug]
  );

  const handleNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const handleDescriptionChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setDescription(event.target.value);
    },
    []
  );

  const handleStartUrlChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setStartUrl(value);
    setUrlValid(httpUrlSchema.safeParse(value).success);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!isValid) {
      setShowErrors(true);
      return;
    }

    onSubmit({
      name: name.trim(),
      description: description.trim(),
      startUrl: startUrl.trim(),
      slug,
    });
  }, [
    isValid,
    onSubmit,
    name,
    description,
    startUrl,
    slug,
  ]);

  const handleDialogChange = useCallback(
    (openState: boolean) => {
      if (!openState) {
        setShowErrors(false);
      }
      onOpenChange(openState);
    },
    [onOpenChange]
  );

  const handleCancel = useCallback(() => {
    handleDialogChange(false);
  }, [handleDialogChange]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogChange}>
        <Dialog.Content className={styles.content} size="large">
          <div className={styles.header}>
            <Dialog.Title>
                <Text className={styles.title}>{mode === "create" ? "Create Browser Skill" : "Edit Browser Skill"}</Text>
            </Dialog.Title>
            <Flex align="center" gap="2">
              <Badge color="orange" variant="soft">
                Browser
              </Badge>
            </Flex>
          </div>

          <div className={styles.body}>
            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <Text size="2" weight="medium">
                  Name
                </Text>
                <TextField.Root
                  size="3"
                  value={name}
                  onChange={handleNameChange}
                />
                {showErrors && !name.trim() && (
                  <Text size="1" className={styles.error}>
                    Name is required.
                  </Text>
                )}
              </div>

              <div className={styles.field}>
                <Text size="2" weight="medium">
                  Start URL
                </Text>
                <TextField.Root
                  size="3"
                  value={startUrl}
                  onChange={handleStartUrlChange}
                />
                <Text size="1" className={styles.helper}>
                  The browsing session will open at this URL.
                </Text>
                {(showErrors || startUrl.length > 0) && !urlValid && (
                  <Text size="1" className={styles.error}>
                    Enter a valid URL with http or https.
                  </Text>
                )}
              </div>
            </div>

            <div className={styles.field}>
              <Text size="2" weight="medium">
                Description
              </Text>
              <TextArea
                value={description}
                onChange={handleDescriptionChange}
                rows={4}
                placeholder="Describe what this browser skill should do."
              />
              {showErrors && !description.trim() && (
                <Text size="1" className={styles.error}>
                  Description is required.
                </Text>
              )}
            </div>

            {isDuplicateSlug && (
              <Text size="1" className={styles.error}>
                Slug {slug} is already in use.
              </Text>
            )}
          </div>

          <div className={styles.footer}>
            <Button variant="soft" color="gray" onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              color="gray"
              highContrast
              onClick={handleSubmit}
              disabled={loading}
            >
              {mode === "create" ? "Create Skill" : "Save Changes"}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog>
    </>
  );
}
